import React, { useEffect, useRef } from 'react';

// Cinematic kawaii backdrop: sakura petals + twinkling stars + bokeh, with mouse parallax.
// ponytail: canvas 2D, no WebGL; particle count scales with viewport area.
const PINKS = ['#FFB7D5', '#FF8FC0', '#FFD1E3', '#FF3CAC'];
const rnd = (a, b) => a + Math.random() * (b - a);

export const Background = () => {
  const ref = useRef(null);

  useEffect(() => {
    const cv = ref.current;
    const ctx = cv.getContext('2d');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    let w = 0, h = 0, raf = 0, t = 0;
    const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
    let petals = [], stars = [], bokeh = [];

    const mkPetal = (top) => ({
      x: rnd(0, w), y: top ? rnd(-60, -10) : rnd(0, h), z: rnd(0.35, 1),
      s: rnd(7, 15), a: rnd(0, 6.28), va: rnd(-0.03, 0.03), sway: rnd(0, 6.28),
      vy: rnd(0.35, 0.9), c: PINKS[(Math.random() * PINKS.length) | 0],
    });
    const build = () => {
      const area = w * h / 1e6;
      petals = Array.from({ length: Math.round(22 * area) + 10 }, () => mkPetal(false));
      stars = Array.from({ length: Math.round(60 * area) + 30 }, () => ({
        x: rnd(0, w), y: rnd(0, h), r: rnd(1, 3.2), p: rnd(0, 6.28), sp: rnd(0.6, 1.8), z: rnd(0.1, 0.5),
        c: Math.random() < 0.7 ? '#FFFFFF' : Math.random() < 0.5 ? '#00F5FF' : '#FFD700',
      }));
      bokeh = Array.from({ length: Math.round(14 * area) + 8 }, () => ({
        x: rnd(0, w), y: rnd(0, h), r: rnd(30, 110), z: rnd(0.05, 0.3), vx: rnd(-0.08, 0.08), vy: rnd(-0.12, -0.03),
        c: Math.random() < 0.5 ? '255,60,172' : Math.random() < 0.5 ? '0,245,255' : '147,51,234',
      }));
    };
    const resize = () => {
      const dpr = Math.min(devicePixelRatio || 1, 2);
      w = innerWidth; h = innerHeight;
      cv.width = w * dpr; cv.height = h * dpr;
      cv.style.width = w + 'px'; cv.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
    };

    const petal = (o, px, py) => {
      ctx.save();
      ctx.translate(o.x + px, o.y + py);
      ctx.rotate(o.a);
      ctx.scale(o.z, o.z);
      ctx.fillStyle = o.c;
      ctx.globalAlpha = 0.35 + o.z * 0.4;
      ctx.beginPath();
      ctx.moveTo(0, -o.s);
      ctx.bezierCurveTo(o.s, -o.s, o.s, o.s * 0.6, 0, o.s);
      ctx.bezierCurveTo(-o.s, o.s * 0.6, -o.s, -o.s, 0, -o.s);
      ctx.fill();
      ctx.restore();
    };
    const star = (o, px, py, tw) => {
      const r = o.r * (0.6 + tw * 0.8);
      ctx.save();
      ctx.translate(o.x + px, o.y + py);
      ctx.globalAlpha = 0.25 + tw * 0.75;
      ctx.fillStyle = o.c;
      ctx.shadowColor = o.c; ctx.shadowBlur = 8 * tw;
      ctx.beginPath();
      for (let i = 0; i < 8; i++) {
        const rr = i % 2 ? r * 0.35 : r * 2.2, an = i * Math.PI / 4;
        ctx.lineTo(Math.cos(an) * rr, Math.sin(an) * rr);
      }
      ctx.closePath(); ctx.fill();
      ctx.restore();
    };

    const frame = () => {
      t += 0.016;
      mouse.x += (mouse.tx - mouse.x) * 0.05; mouse.y += (mouse.ty - mouse.y) * 0.05;
      ctx.clearRect(0, 0, w, h);

      for (const b of bokeh) {
        b.x += b.vx; b.y += b.vy;
        if (b.y < -b.r) { b.y = h + b.r; b.x = rnd(0, w); }
        const g = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, b.r);
        g.addColorStop(0, 'rgba(' + b.c + ',.16)'); g.addColorStop(1, 'rgba(' + b.c + ',0)');
        ctx.fillStyle = g;
        ctx.fillRect(b.x - b.r + mouse.x * b.z, b.y - b.r + mouse.y * b.z, b.r * 2, b.r * 2);
      }
      for (const s of stars) star(s, mouse.x * s.z, mouse.y * s.z, (Math.sin(t * s.sp + s.p) + 1) / 2);
      for (let i = 0; i < petals.length; i++) {
        const o = petals[i];
        o.y += o.vy * o.z * 1.6; o.x += Math.sin(t * 0.8 + o.sway) * 0.5 * o.z; o.a += o.va;
        if (o.y > h + 30) petals[i] = mkPetal(true);
        petal(o, mouse.x * o.z * 1.4, mouse.y * o.z * 1.4);
      }
      raf = requestAnimationFrame(frame);
    };

    const onMove = (e) => { mouse.tx = (e.clientX / w - 0.5) * -40; mouse.ty = (e.clientY / h - 0.5) * -40; };
    const onVis = () => { cancelAnimationFrame(raf); if (!document.hidden && !reduced) raf = requestAnimationFrame(frame); };

    resize();
    addEventListener('resize', resize);
    if (reduced) { frame(); cancelAnimationFrame(raf); } // single static frame
    else { addEventListener('pointermove', onMove); document.addEventListener('visibilitychange', onVis); raf = requestAnimationFrame(frame); }
    return () => {
      cancelAnimationFrame(raf);
      removeEventListener('resize', resize); removeEventListener('pointermove', onMove);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return (
    <>
      <div className="bg-aurora" />
      <div className="bg-rays" />
      <canvas ref={ref} className="bg-canvas" aria-hidden="true" />
      <div className="bg-vignette" />
    </>
  );
};
